import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, RefreshCw, Check, X, Edit3, Loader2,
  Trash2, AlertTriangle, CheckCircle2, ImagePlus,
  Save, Square, CheckSquare, ArrowLeft,
  ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "bulkUploadPipeline";

const BulkUploadPanel = ({ adminToken, onSessionExpired }) => {
  const handle401 = (err) => {
    if (err?.response?.status === 401) {
      toast.error("Session expired — please log in again");
      if (onSessionExpired) onSessionExpired();
      return true;
    }
    return false;
  };

  const [phase, setPhase] = useState("upload"); // upload | processing | review
  const [meals, setMeals] = useState([]);
  const [mealStatus, setMealStatus] = useState({});
  const [selectedForInsert, setSelectedForInsert] = useState({});
  const [inserting, setInserting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const pipelineRunning = useRef(false);

  // Auto-reset to upload when all meals deleted
  useEffect(() => {
    if (meals.length === 0 && phase !== "upload") {
      setPhase("upload");
    }
  }, [meals, phase]);

  // Persist state
  useEffect(() => {
    if (meals.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ meals, mealStatus, phase }));
    }
  }, [meals, mealStatus, phase]);

  // Restore state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { meals: m, mealStatus: ms, phase: p } = JSON.parse(saved);
        if (m?.length > 0) { setMeals(m); setMealStatus(ms || {}); setPhase(p || "processing"); }
      }
    } catch (e) { /* ignore */ }
  }, []);

  // ── Helpers ──
  const deleteMeal = (id) => {
    setMeals(prev => prev.filter(m => m.meal_id !== id));
    setMealStatus(prev => { const n = { ...prev }; delete n[id]; return n; });
    setSelectedForInsert(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const updateDetail = (id, field, value) => {
    setMealStatus(prev => ({
      ...prev,
      [id]: { ...prev[id], details: { ...prev[id]?.details, [field]: value } }
    }));
  };

  const clearAll = () => {
    setMeals([]);
    setMealStatus({});
    setSelectedForInsert({});
    setPhase("upload");
    setEditingId(null);
    setExpandedId(null);
    pipelineRunning.current = false;
    localStorage.removeItem(STORAGE_KEY);
    toast.success("Cleared — ready for new upload");
  };

  // ── Upload Excel ──
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/admin/bulk-upload?token=${adminToken}`, fd);
      if (res.data.success && res.data.meals.length > 0) {
        setMeals(res.data.meals);
        setMealStatus({});
        setSelectedForInsert({});
        toast.success(`Found ${res.data.count} meals`);
        setPhase("processing");
        setTimeout(() => runPipeline(res.data.meals), 500);
      } else {
        toast.error("No valid meals found");
      }
    } catch (err) { if (!handle401(err)) toast.error("Failed to parse file"); }
    setUploading(false);
    e.target.value = "";
  };

  // ── Pipeline (details only — no image gen) ──
  const runPipeline = async (mealsList) => {
    if (pipelineRunning.current) return;
    pipelineRunning.current = true;

    for (const meal of mealsList) {
      if (!pipelineRunning.current) break;
      if (meal.is_duplicate) continue;
      const id = meal.meal_id;

      setMealStatus(prev => ({ ...prev, [id]: { ...prev[id], detailsLoading: true } }));
      try {
        const res = await axios.post(`${API}/api/admin/bulk-generate-details?token=${adminToken}`, meal, { timeout: 120000 });
        if (res.data.success) {
          setMealStatus(prev => ({ ...prev, [id]: { ...prev[id], details: res.data.meal, detailsLoading: false } }));
        }
      } catch (err) {
        if (handle401(err)) break;
        setMealStatus(prev => ({ ...prev, [id]: { ...prev[id], detailsLoading: false, error: "Details failed — tap Retry" } }));
      }
    }
    pipelineRunning.current = false;
  };

  // ── Retry single meal ──
  const retryMeal = async (meal) => {
    const id = meal.meal_id;
    setMealStatus(prev => ({ ...prev, [id]: { ...prev[id], error: null, detailsLoading: true } }));
    try {
      const res = await axios.post(`${API}/api/admin/bulk-generate-details?token=${adminToken}`, meal, { timeout: 60000 });
      if (res.data.success) setMealStatus(prev => ({ ...prev, [id]: { ...prev[id], details: res.data.meal, detailsLoading: false } }));
    } catch (err) {
      if (!handle401(err)) {
        setMealStatus(prev => ({ ...prev, [id]: { ...prev[id], detailsLoading: false, error: "Details failed again" } }));
      }
    }
  };

  // ── Upload image for a meal ──
  const handleImageUpload = async (mealId, file) => {
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPG or PNG images are allowed");
      return;
    }

    setMealStatus(prev => ({ ...prev, [mealId]: { ...prev[mealId], imageUploading: true } }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/admin/bulk-upload-image?token=${adminToken}&meal_id=${mealId}`, fd, { timeout: 30000 });
      if (res.data.success) {
        setMealStatus(prev => ({
          ...prev,
          [mealId]: { ...prev[mealId], imageUrl: res.data.image_url, imageUploading: false }
        }));
        toast.success("Image uploaded");
      }
    } catch (err) {
      if (!handle401(err)) toast.error("Image upload failed");
      setMealStatus(prev => ({ ...prev, [mealId]: { ...prev[mealId], imageUploading: false } }));
    }
  };

  // ── Insert ──
  const toggleSelect = (id) => setSelectedForInsert(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleSelectAll = () => {
    const eligible = meals.filter(m => mealStatus[m.meal_id]?.details && mealStatus[m.meal_id]?.imageUrl);
    const allSel = eligible.every(m => selectedForInsert[m.meal_id]);
    const ns = {};
    eligible.forEach(m => { ns[m.meal_id] = !allSel; });
    setSelectedForInsert(prev => ({ ...prev, ...ns }));
  };

  const handleInsert = async () => {
    const toInsert = meals.filter(m => selectedForInsert[m.meal_id] && mealStatus[m.meal_id]?.details && mealStatus[m.meal_id]?.imageUrl).map(m => {
      const data = { ...mealStatus[m.meal_id].details };
      data.image_url = mealStatus[m.meal_id].imageUrl;
      return data;
    });
    if (toInsert.length === 0) { toast.error("No meals selected"); return; }
    setInserting(true);
    try {
      const res = await axios.post(`${API}/api/admin/bulk-insert?token=${adminToken}`, toInsert);
      if (res.data.success) { toast.success(res.data.message); clearAll(); }
    } catch (err) { if (!handle401(err)) toast.error("Failed to insert"); }
    setInserting(false);
  };

  // ── Computed ──
  const activeMeals = meals.filter(m => !m.is_duplicate);
  const detailsDone = activeMeals.filter(m => mealStatus[m.meal_id]?.details).length;
  const imagesDone = activeMeals.filter(m => mealStatus[m.meal_id]?.imageUrl).length;
  const anyLoading = activeMeals.some(m => mealStatus[m.meal_id]?.detailsLoading);
  const selectedCount = Object.values(selectedForInsert).filter(Boolean).length;
  const readyCount = activeMeals.filter(m => mealStatus[m.meal_id]?.details && mealStatus[m.meal_id]?.imageUrl).length;

  // ──────── MEAL CARD ────────
  const MealCard = ({ meal }) => {
    const id = meal.meal_id;
    const s = mealStatus[id] || {};
    const d = s.details;
    const hasImage = !!s.imageUrl;
    const isEditing = editingId === id;
    const isExpanded = expandedId === id;

    let statusColor = "bg-muted/30";
    let statusText = "Queued";
    if (s.error) { statusColor = "bg-red-50 border-red-200"; statusText = "Error"; }
    else if (d && hasImage) { statusColor = "bg-green-50 border-green-200"; statusText = "Ready"; }
    else if (d && !hasImage) { statusColor = "bg-amber-50 border-amber-200"; statusText = "Needs image"; }
    else if (s.detailsLoading) { statusColor = "bg-primary/5 border-primary/20"; statusText = "Processing"; }

    return (
      <div className={`rounded-2xl border overflow-hidden transition-all ${statusColor}`} data-testid={`meal-card-${id}`}>

        {/* Card Header */}
        <div className="px-4 py-3 flex items-center gap-3 bg-white/80 backdrop-blur-sm">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
            s.error ? "bg-red-500" : d && hasImage ? "bg-green-500" : d ? "bg-amber-500" : s.detailsLoading ? "bg-primary animate-pulse" : "bg-muted-foreground/20"
          }`} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{meal.meal_name}</p>
            <p className="text-[10px] text-muted-foreground">{statusText}</p>
          </div>
          {meal.is_duplicate && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">In DB</span>}
          <Button size="sm" variant="ghost" onClick={() => deleteMeal(id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0" data-testid={`delete-${id}`}>
            <Trash2 size={13} />
          </Button>
        </div>

        {/* Error State */}
        {s.error && !s.detailsLoading && (
          <div className="px-4 py-3 flex items-center gap-2 bg-red-50">
            <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-600 flex-1">{s.error}</span>
            <Button size="sm" variant="outline" onClick={() => retryMeal(meal)} className="text-xs h-7 px-3 gap-1 border-red-200 text-red-600 hover:bg-red-100" data-testid={`retry-${id}`}>
              <RefreshCw size={10} />Retry
            </Button>
          </div>
        )}

        {/* Loading */}
        {s.detailsLoading && (
          <div className="px-4 py-5 flex items-center justify-center gap-2 bg-white/50">
            <Loader2 size={16} className="animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Generating meal details...</span>
          </div>
        )}

        {/* Details + Image Upload Section */}
        {d && !s.detailsLoading && (
          <div className="bg-white/60">
            {/* Quick stats */}
            <div className="px-4 py-2.5 flex gap-1.5 flex-wrap items-center">
              <select value={d.primary_meal_type || "Lunch"}
                onChange={(e) => updateDetail(id, "primary_meal_type", e.target.value)}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border-none cursor-pointer appearance-none pr-4 outline-none"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%231d4ed8' stroke-width='3'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center" }}
                data-testid={`primary-type-${id}`}>
                <option value="Breakfast">Breakfast</option>
                <option value="Lunch">Lunch</option>
                <option value="Dinner">Dinner</option>
              </select>
              <select value={d.secondary_meal_type && d.secondary_meal_type !== "null" && d.secondary_meal_type !== "None" ? d.secondary_meal_type : ""}
                onChange={(e) => updateDetail(id, "secondary_meal_type", e.target.value || null)}
                className={`text-[10px] font-medium px-2 py-0.5 rounded-full border cursor-pointer appearance-none pr-4 outline-none ${
                  d.secondary_meal_type && d.secondary_meal_type !== "null" && d.secondary_meal_type !== "None" ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-muted/20 text-muted-foreground border-border/40"
                }`}
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='3'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center" }}
                data-testid={`secondary-type-${id}`}>
                <option value="">No secondary</option>
                <option value="Breakfast">Also: Breakfast</option>
                <option value="Lunch">Also: Lunch</option>
                <option value="Dinner">Also: Dinner</option>
              </select>
              {[
                { label: d.base, color: "bg-purple-100 text-purple-700" },
                { label: `${d.calories} kcal`, color: "bg-orange-100 text-orange-700" },
                { label: `${d.protein}g protein`, color: "bg-green-100 text-green-700" },
                { label: `${d.prep_time_min} min`, color: "bg-slate-100 text-slate-600" },
              ].map((t, i) => <span key={i} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${t.color}`}>{t.label}</span>)}
              {d.ai_verified && <CheckCircle2 size={12} className="text-green-500" />}
            </div>

            {/* Notes + ingredients preview */}
            <div className="px-4 pb-2">
              {d.notes && <p className="text-xs text-muted-foreground italic mb-2">{d.notes}</p>}
              <div className="flex flex-wrap gap-1 mb-2">
                {(d.ingredients || []).slice(0, 6).map((ing, i) => (
                  <span key={i} className="text-[10px] bg-muted/40 text-muted-foreground px-1.5 py-0.5 rounded">{ing}</span>
                ))}
                {(d.ingredients || []).length > 6 && <span className="text-[10px] text-muted-foreground">+{d.ingredients.length - 6} more</span>}
              </div>
            </div>

            {/* IMAGE UPLOAD SECTION */}
            <div className="px-4 py-3 border-t border-border/20">
              {hasImage ? (
                <div className="flex items-center gap-3">
                  <img src={`${API}${s.imageUrl}?t=${Date.now()}`} alt={meal.meal_name}
                    className="w-20 h-20 rounded-xl object-cover border border-border/30" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-green-700 flex items-center gap-1 mb-1.5">
                      <CheckCircle2 size={12} />Image uploaded
                    </p>
                    <label className="inline-flex items-center gap-1 px-2.5 py-1 bg-muted/40 hover:bg-muted/60 rounded-lg cursor-pointer text-[11px] text-muted-foreground transition-colors">
                      <RefreshCw size={10} />Replace
                      <input type="file" accept="image/jpeg,image/png,image/jpg" className="hidden"
                        onChange={(e) => handleImageUpload(id, e.target.files[0])} data-testid={`replace-img-${id}`} />
                    </label>
                  </div>
                </div>
              ) : (
                <label className={`flex flex-col items-center justify-center py-5 border-2 border-dashed rounded-xl cursor-pointer transition-all
                  ${s.imageUploading ? "border-primary/30 bg-primary/5" : "border-border/40 hover:border-primary/40 hover:bg-primary/5"}`}
                  data-testid={`upload-img-${id}`}>
                  {s.imageUploading ? (
                    <>
                      <Loader2 size={20} className="text-primary animate-spin mb-1" />
                      <span className="text-xs text-muted-foreground">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <ImagePlus size={20} className="text-muted-foreground/50 mb-1" />
                      <span className="text-xs font-medium text-muted-foreground">Upload Image</span>
                      <span className="text-[10px] text-muted-foreground/60 mt-0.5">JPG or PNG</span>
                    </>
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/jpg" className="hidden"
                    disabled={s.imageUploading}
                    onChange={(e) => handleImageUpload(id, e.target.files[0])} />
                </label>
              )}
            </div>

            {/* Expand for full details */}
            <button onClick={() => setExpandedId(isExpanded ? null : id)}
              className="w-full px-4 py-1.5 text-[10px] text-primary hover:bg-primary/5 flex items-center justify-center gap-1 border-t border-border/20" data-testid={`expand-${id}`}>
              {isExpanded ? <><ChevronUp size={10} />Less details</> : <><ChevronDown size={10} />Full details &amp; steps</>}
            </button>

            {isExpanded && (
              <div className="px-4 py-3 space-y-3 border-t border-border/20 bg-white/80">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Ingredients</p>
                    <button onClick={() => setEditingId(isEditing ? null : id)} className="text-[10px] text-primary hover:underline flex items-center gap-0.5" data-testid={`edit-${id}`}>
                      <Edit3 size={9} />{isEditing ? "Done" : "Edit"}
                    </button>
                  </div>
                  {isEditing ? (
                    <textarea className="w-full text-xs bg-white border border-border rounded-lg px-3 py-2 resize-none" rows={4}
                      value={(d.ingredients || []).join("\n")} onChange={(e) => updateDetail(id, "ingredients", e.target.value.split("\n").filter(l => l.trim()))} />
                  ) : (
                    <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
                      {(d.ingredients || []).map((ing, i) => <li key={i}>{ing}</li>)}
                    </ul>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Steps</p>
                  {isEditing ? (
                    <textarea className="w-full text-xs bg-white border border-border rounded-lg px-3 py-2 resize-none" rows={4}
                      value={(d.steps || []).join("\n")} onChange={(e) => updateDetail(id, "steps", e.target.value.split("\n").filter(l => l.trim()))} />
                  ) : (
                    <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                      {(d.steps || []).map((st, i) => <li key={i}>{st}</li>)}
                    </ol>
                  )}
                </div>

                {isEditing && (
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "Cuisine", field: "cuisine" },
                      { label: "Calories", field: "calories" },
                      { label: "Protein", field: "protein" },
                      { label: "Prep min", field: "prep_time_min" },
                    ].map(({ label, field }) => (
                      <div key={field}>
                        <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
                        <input className="w-full text-xs bg-white border border-border rounded px-2 py-1"
                          value={d[field] ?? ""} onChange={(e) => updateDetail(id, field, ["calories", "protein", "prep_time_min"].includes(field) ? parseInt(e.target.value) || 0 : e.target.value)} />
                      </div>
                    ))}
                  </div>
                )}

                <Button size="sm" variant="outline" onClick={() => retryMeal(meal)} className="text-xs h-7 gap-1 w-full" data-testid={`regen-details-${id}`}>
                  <RefreshCw size={10} />Regenerate details
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Queued */}
        {!d && !s.detailsLoading && !s.error && !meal.is_duplicate && (
          <div className="px-4 py-3 flex items-center gap-2 text-muted-foreground/40 bg-white/30">
            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/20" />
            <span className="text-[11px]">Waiting in queue...</span>
          </div>
        )}
      </div>
    );
  };

  // ──────── REVIEW CARD ────────
  const ReviewCard = ({ meal }) => {
    const id = meal.meal_id;
    const s = mealStatus[id] || {};
    const d = s.details;
    if (!d || !s.imageUrl) return null;

    return (
      <div className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all
        ${selectedForInsert[id] ? "border-green-300 ring-1 ring-green-100" : "border-border/40"}`} data-testid={`review-${id}`}>
        <div className="flex items-center gap-3 p-3">
          <button onClick={() => toggleSelect(id)} className="flex-shrink-0" data-testid={`review-check-${id}`}>
            {selectedForInsert[id] ? <CheckSquare size={20} className="text-green-600" /> : <Square size={20} className="text-muted-foreground/30" />}
          </button>
          <div className="w-12 h-12 rounded-lg bg-muted/30 flex-shrink-0 overflow-hidden">
            <img src={`${API}${s.imageUrl}`} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{d.meal_name}</p>
            <div className="flex gap-2 mt-0.5 text-[10px] text-muted-foreground">
              <span>{d.primary_meal_type}</span><span>{d.calories} kcal</span><span>{d.protein}g</span>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => deleteMeal(id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0" data-testid={`review-del-${id}`}>
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    );
  };

  // ──────── RENDER ────────
  return (
    <div className="space-y-4" data-testid="bulk-upload-panel">

      {/* UPLOAD */}
      {phase === "upload" && (
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 border border-border/40 text-center" data-testid="upload-area">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Upload size={24} className="text-primary" />
          </div>
          <h2 className="text-base font-semibold mb-1">Upload Meals</h2>
          <p className="text-muted-foreground text-sm mb-1">Upload an Excel file with dish names</p>
          <p className="text-muted-foreground text-xs mb-6">One column, one dish per row — e.g., "Bhindi + Daal + Roti"</p>
          <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl cursor-pointer hover:bg-primary/90 transition-colors font-medium text-sm" data-testid="upload-btn">
            <Upload size={16} />{uploading ? "Uploading..." : "Choose Excel File"}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="bulk-file-input" />
          </label>
        </div>
      )}

      {/* PROCESSING */}
      {phase === "processing" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-border/40 p-3 shadow-sm" data-testid="processing-status">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">{activeMeals.length} Meals</h2>
                  {anyLoading && <Loader2 size={13} className="animate-spin text-primary" />}
                </div>
                <div className="flex gap-3 mt-0.5 text-[10px] text-muted-foreground">
                  <span>{detailsDone} details</span>
                  <span>{imagesDone} images</span>
                  <span>{readyCount} ready</span>
                </div>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={clearAll} className="text-xs h-7 px-2 gap-1" data-testid="new-upload-btn">
                  <Upload size={10} />New
                </Button>
                {readyCount > 0 && (
                  <Button size="sm" onClick={() => {
                    meals.filter(m => mealStatus[m.meal_id]?.details && mealStatus[m.meal_id]?.imageUrl).forEach(m => {
                      setSelectedForInsert(prev => ({ ...prev, [m.meal_id]: true }));
                    });
                    setPhase("review");
                  }} className="text-xs h-7 gap-1" data-testid="go-review-btn">
                    <Save size={10} />Review & Save
                  </Button>
                )}
              </div>
            </div>
            {anyLoading && (
              <div className="w-full h-1 bg-muted/30 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${activeMeals.length > 0 ? Math.round((detailsDone / activeMeals.length) * 100) : 0}%` }} />
              </div>
            )}
          </div>

          <div className="space-y-3">
            {meals.map(meal => <MealCard key={meal.meal_id} meal={meal} />)}
          </div>
        </div>
      )}

      {/* REVIEW */}
      {phase === "review" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-border/40 p-3 shadow-sm" data-testid="review-status">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold">Review & Save</h2>
                <p className="text-[10px] text-muted-foreground mt-0.5">{selectedCount} of {readyCount} selected</p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setPhase("processing")} className="text-xs h-7 gap-1">
                  <ArrowLeft size={10} />Back
                </Button>
                <Button size="sm" variant="outline" onClick={toggleSelectAll} className="text-xs h-7 gap-1" data-testid="toggle-all-btn">
                  {meals.filter(m => mealStatus[m.meal_id]?.details && mealStatus[m.meal_id]?.imageUrl).every(m => selectedForInsert[m.meal_id]) ? <><Square size={10} />Deselect</> : <><CheckSquare size={10} />Select All</>}
                </Button>
                <Button size="sm" onClick={handleInsert} disabled={inserting || selectedCount === 0} className="text-xs h-7 gap-1" data-testid="insert-btn">
                  {inserting ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                  Add {selectedCount}
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {meals.filter(m => mealStatus[m.meal_id]?.details && mealStatus[m.meal_id]?.imageUrl).map(meal => <ReviewCard key={meal.meal_id} meal={meal} />)}
          </div>
          {activeMeals.filter(m => mealStatus[m.meal_id]?.details && !mealStatus[m.meal_id]?.imageUrl).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 flex items-center gap-2" data-testid="no-img-warning">
              <AlertTriangle size={13} className="text-amber-500 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">{activeMeals.filter(m => mealStatus[m.meal_id]?.details && !mealStatus[m.meal_id]?.imageUrl).length} meal(s) still need images — go back to upload them</p>
            </div>
          )}
        </div>
      )}

      {/* Clear */}
      {meals.length > 0 && (
        <div className="text-center pt-1">
          <button onClick={clearAll} className="text-[11px] text-muted-foreground hover:text-red-500 transition-colors flex items-center gap-1 mx-auto" data-testid="clear-all-btn">
            <X size={11} />Clear all and start fresh
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkUploadPanel;
